import type { ColumnState, GridApi } from 'ag-grid-community'
import React, { useEffect, useRef, useState } from 'react'

import { useReportErrorsCallback } from '@/lib/connect/error-reporting'
import {
  getFilterSourceParams,
  getStoredSourceParams,
  writeStoredFilterParam,
  writeStoredSort,
} from '@/lib/storage/local-storage.hook'
import type { ReplaceParamsOptions } from '@/lib/url/base-url-param.hook'

import { CANONICAL_PARAM_KEYS } from '@/components/lemmala-design-system/table/consts'
import type { FilterModel, UrlSyncManager } from '@/components/lemmala-design-system/table/types'
import {
  appliesFilterModel,
  withoutEmptySetFilters,
} from '@/components/lemmala-design-system/table/utils/filter-model'
import {
  applySortModelViaColumnState,
  getColumnStateFromGrid,
  getSortModelFromColumnState,
} from '@/components/lemmala-design-system/table/utils/grid-state'
import {
  columnStateToUrlParam as defaultColumnStateToUrlParam,
  getFilterUrlUpdates as defaultGetFilterUrlUpdates,
  sortModelToUrlParam as defaultSortModelToUrlParam,
  urlParamToColumnState as defaultUrlParamToColumnState,
  urlParamToSortModel as defaultUrlParamToSortModel,
  urlToFilterModel as defaultUrlToFilterModel,
  toDefinedParams,
} from '@/components/lemmala-design-system/table/utils/url-serializers'

/**
 * Configuration for URL synchronization functionality.
 */
export interface UrlSyncConfig {
  /**
   * Whether URL synchronization is enabled.
   * @default true
   */
  enabled?: boolean

  /**
   * Prefix for URL parameters to avoid conflicts between multiple tables.
   * When specified, all URL parameters will be prefixed with this value.
   * @example "table1" -> "table1_SEARCH", "table1_FILTER", etc.
   */
  prefix?: string

  /**
   * URL parameter management functions.
   */
  urlManager: {
    /** Current URL parameters */
    urlParams: Record<string, string | undefined>
    /** Function to update URL parameters */
    replaceParams: (updates: ReplaceParamsOptions) => void
  }

  /**
   * URL serialization functions for filters and sort models.
   */
  urlSerializers?: {
    /** Convert filter model to URL parameter updates */
    getFilterUrlUpdates?: (filterModel: FilterModel) => {
      update: Array<[string, string]>
      remove: string[]
    }
    /** Convert URL params to filter model */
    urlToFilterModel?: (params: Record<string, string | undefined>) => FilterModel
    /** Convert sort model to URL parameter string */
    sortModelToUrlParam?: (
      sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>,
    ) => string | null
    /** Convert URL param to sort model */
    urlParamToSortModel?: (sortParam: string) => Array<{ colId: string; sort: 'asc' | 'desc' }>
    /** Convert column state to URL parameter string */
    columnStateToUrlParam?: (columnState: ColumnState[]) => string | null
    /** Convert URL param to column state */
    urlParamToColumnState?: (
      urlParam: string | null | undefined,
      allColumnIds: string[],
    ) => Partial<ColumnState>[] | null
  }

  /**
   * URL parameter names/keys to use.
   */
  urlParamKeys: {
    search?: string
    view?: string
    filter?: string
    risk?: string // legacy, unused in canonical model
    status?: string // legacy, unused in canonical model
    sort?: string
    columns?: string
    [key: string]: string | undefined
  }

  /**
   * All available column IDs for column state restoration.
   * Required for column customization to work properly.
   */
  allColumnIds?: string[]

  /**
   * Current view name and view application state tracking.
   */
  viewState: {
    currentView: string
    isApplyingViewRef: React.MutableRefObject<boolean>
  }

  /**
   * Search text state and handlers.
   */
  searchState?: {
    quickFilterText: string
    onSearchChange: (text: string) => void
  }

  /**
   * Debounce delays in milliseconds.
   */
  debounceDelays?: {
    /** Delay for URL updates to prevent spam */
    urlUpdate?: number
    /** Delay for search text updates */
    searchUpdate?: number
    /** Delay before setting up event listeners */
    listenerSetup?: number
  }

  /**
   * Default view name for restoration logic.
   * @default "All"
   */
  defaultView?: string
  /**
   * When true, apply SEARCH text to AG Grid's quick filter automatically.
   * Leave false for setups that implement external fuzzy search.
   */
  applyQuickFilter?: boolean

  /**
   * `{userId}_{tenantId}:{tablePrefix}` key for persisted filters (composed by
   * AgDataTable). Undefined disables filter persistence.
   */
  filterStorageKey?: string
}

/**
 * Generic URL synchronization hook for AG Grid tables.
 *
 * This hook provides bidirectional synchronization between AG Grid state (filters, sort, search)
 * and URL parameters with configurable serialization, debouncing, and conflict prevention.
 *
 * Features:
 * - Bidirectional sync of filters, sort, and search text with URL
 * - Configurable debouncing to prevent URL spam during rapid changes
 * - Conflict prevention with view management system
 * - Proper event listener setup/cleanup with timing delays
 * - URL restoration from parameters with error handling
 * - Support for view preservation during filter/sort changes
 *
 * @example
 * ```tsx
 * const urlSync = useAgUrlSync(gridApi, {
 *   urlManager: {
 *     urlParams: useBaseUrlParams().urlParams,
 *     replaceParams: useBaseUrlParams().replaceParams,
 *   },
 *   urlSerializers: {
 *     getFilterUrlUpdates: getFilterUrlUpdates,
 *     urlToFilterModel: urlParamsToFilterModel,
 *     sortModelToUrlParam: sortModelToUrlParam,
 *     urlParamToSortModel: urlParamToSortModel,
 *   },
 *   urlParamKeys: {
 *     search: 'SEARCH',
 *     view: 'VIEW',
 *     filter: 'FILTER',
 *     sort: 'SORT',
 *   },
 *   viewState: {
 *     currentView: viewManager.currentView,
 *     isApplyingViewRef: viewManager.isApplyingViewRef,
 *   },
 *   searchState: {
 *     quickFilterText: searchText,
 *     onSearchChange: setSearchText,
 *   },
 * })
 * ```
 */
export const useAgUrlSync = <TRowData = unknown>(
  gridApi: GridApi<TRowData> | null,
  config: UrlSyncConfig,
): UrlSyncManager => {
  const {
    enabled = true,
    prefix,
    urlManager,
    urlSerializers,
    urlParamKeys,
    viewState,
    searchState,
    debounceDelays = {},
    defaultView = 'All',
    applyQuickFilter = true,
    allColumnIds = [],
    filterStorageKey,
  } = config

  const { urlParams, replaceParams } = urlManager
  const { currentView, isApplyingViewRef } = viewState
  const reportErrors = useReportErrorsCallback()

  // Helper function to apply prefix to parameter keys
  const applyPrefix = React.useCallback(
    (key: string | undefined): string | undefined => {
      if (!key) return key
      return prefix ? `${prefix}_${key}` : key
    },
    [prefix],
  )

  // Default delays
  const delays = {
    urlUpdate: debounceDelays.urlUpdate || 300,
    searchUpdate: debounceDelays.searchUpdate || 300,
    listenerSetup: debounceDelays.listenerSetup || 500,
  }

  // State tracking (use state for values that need to trigger re-renders)
  const [isRestored, setIsRestored] = useState(false)
  const [isUpdatingUrl, setIsUpdatingUrl] = useState(false)

  // Refs for values that don't need to trigger re-renders
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const viewInitializedRef = useRef(false)
  const urlParamsRef = useRef(urlParams)
  const replaceParamsRef = useRef(replaceParams)

  // Provide defaults for serializers and param keys (canonical model)
  const keys = React.useMemo(
    () => ({
      search: applyPrefix(urlParamKeys.search || CANONICAL_PARAM_KEYS.search),
      view: applyPrefix(urlParamKeys.view || CANONICAL_PARAM_KEYS.view),
      filter: applyPrefix(urlParamKeys.filter || CANONICAL_PARAM_KEYS.filter),
      risk: applyPrefix(urlParamKeys.risk || CANONICAL_PARAM_KEYS.legacyRisk),
      status: applyPrefix(urlParamKeys.status || CANONICAL_PARAM_KEYS.legacyStatus),
      sort: applyPrefix(urlParamKeys.sort || CANONICAL_PARAM_KEYS.sort),
      columns: applyPrefix(urlParamKeys.columns || CANONICAL_PARAM_KEYS.columns),
    }),
    [urlParamKeys, applyPrefix],
  )
  const serializers = React.useMemo(
    () => ({
      getFilterUrlUpdates:
        urlSerializers?.getFilterUrlUpdates ||
        ((fm: FilterModel) =>
          defaultGetFilterUrlUpdates(fm, {
            filter: keys.filter!,
            legacyRisk: keys.risk,
            legacyStatus: keys.status,
          })),
      urlToFilterModel:
        urlSerializers?.urlToFilterModel ||
        ((p: Record<string, string | undefined>) =>
          defaultUrlToFilterModel(toDefinedParams(p), {
            filter: keys.filter!,
            legacyRisk: keys.risk,
            legacyStatus: keys.status,
          })),
      sortModelToUrlParam: urlSerializers?.sortModelToUrlParam || defaultSortModelToUrlParam,
      urlParamToSortModel: urlSerializers?.urlParamToSortModel || defaultUrlParamToSortModel,
      columnStateToUrlParam: urlSerializers?.columnStateToUrlParam || defaultColumnStateToUrlParam,
      urlParamToColumnState: urlSerializers?.urlParamToColumnState || defaultUrlParamToColumnState,
    }),
    [urlSerializers, keys],
  )

  const keysRef = useRef(keys)
  const serializersRef = useRef(serializers)
  const allColumnIdsRef = useRef(allColumnIds)
  const filterStorageKeyRef = useRef(filterStorageKey)

  useEffect(() => {
    urlParamsRef.current = urlParams
    replaceParamsRef.current = replaceParams
    keysRef.current = keys
    serializersRef.current = serializers
    allColumnIdsRef.current = allColumnIds
    filterStorageKeyRef.current = filterStorageKey
  }, [urlParams, replaceParams, keys, serializers, allColumnIds, filterStorageKey])

  // Sync search text from URL on mount (if search state is provided)
  useEffect(() => {
    if (!enabled || !searchState || !urlParamKeys.search) return void 0

    // Prevent triggering search if URL param is the same as current text
    if (isUpdatingUrl) {
      return void 0
    }

    const searchFromUrl = keys.search ? urlParams[keys.search] : undefined
    if (searchFromUrl) {
      searchState.onSearchChange(decodeURIComponent(searchFromUrl))
    }
    return void 0
  }, [enabled, urlParams, searchState, isUpdatingUrl, keys.search, urlParamKeys.search])

  // Sync search text to URL when it changes (debounced)
  useEffect(() => {
    if (!enabled || !searchState || !urlParamKeys.search) {
      return void 0
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      setIsUpdatingUrl(true)
      if (searchState.quickFilterText) {
        replaceParams({
          update: [[keys.search!, encodeURIComponent(searchState.quickFilterText)]],
          replaceCurrentHistoryEntry: true,
        })
      } else {
        replaceParams({
          remove: [keys.search!],
          replaceCurrentHistoryEntry: true,
        })
      }
      // Reset updating state after a brief delay
      setTimeout(() => setIsUpdatingUrl(false), 10)
    }, delays.searchUpdate)

    return (): void => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [
    enabled,
    searchState?.quickFilterText,
    replaceParams,
    delays.searchUpdate,
    searchState,
    keys.search,
    urlParamKeys.search,
  ])

  // Apply quick filter to grid if requested
  useEffect(() => {
    if (!gridApi || !searchState || !applyQuickFilter) return
    // grid API uses setQuickFilter via any cast in some versions; fall back safely
    const api = gridApi as { setQuickFilter?: (text: string) => void }
    api.setQuickFilter?.(searchState.quickFilterText)
  }, [gridApi, searchState, searchState?.quickFilterText, applyQuickFilter])

  // Sync filter/sort/columns to the URL. Config that changes every render
  // (keys, serializers, urlParams) is read from refs so we don't tear the
  // listeners down for listenerSetup ms after a column URL write.
  useEffect(() => {
    if (!enabled || !gridApi) {
      return void 0
    }

    const onFilterChanged = (): void => {
      // Skip URL sync if a view is being applied to prevent conflicts
      if (isApplyingViewRef.current) {
        return
      }

      // Drop set filters left empty; re-applying fires filterChanged again, which writes the result.
      const model = gridApi.getFilterModel() as FilterModel
      const cleaned = withoutEmptySetFilters(model)
      if (Object.keys(cleaned).length < Object.keys(model).length) {
        gridApi.setFilterModel(cleaned)
        gridApi.onFilterChanged()
        return
      }

      // Debounce URL updates to prevent excessive history entries
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        const keys = keysRef.current
        const serializers = serializersRef.current
        const filterStorageKey = filterStorageKeyRef.current
        const fullModel = gridApi.getFilterModel() || {}
        // Exclude baseline keys (e.g., *Base) from URL serialization
        const filterModel = Object.fromEntries(
          Object.entries(fullModel).filter(([key]) => !key.endsWith('Base')),
        )
        const currentViewParam = keys.view && urlParamsRef.current[keys.view]

        if (Object.keys(filterModel).length > 0) {
          const { update, remove } = serializers.getFilterUrlUpdates(filterModel)

          // Persist filter values for cross-route navigation (URL still wins on entry)
          if (keys.filter) {
            const filterParam = update.find(([param]) => param === keys.filter)?.[1] ?? null
            writeStoredFilterParam(filterStorageKey, filterParam)
          }

          // If we have a view, preserve it and filter out view-related filters
          if (currentViewParam && keys.view) {
            update.push([keys.view, currentViewParam])

            // Remove status from individual filters if we have a view
            // because status is controlled by the view definition
            if (keys.status) {
              const statusIndex = update.findIndex(([param]) => param === (keys.status as string))
              if (statusIndex !== -1) {
                update.splice(statusIndex, 1)
              }
            }
          } else if (keys.status) {
            // No view specified, preserve individual status parameter if it exists
            const currentStatus = urlParamsRef.current[keys.status]
            if (currentStatus) {
              update.push([keys.status, currentStatus])
            }
          }

          replaceParamsRef.current({
            update,
            remove,
            replaceCurrentHistoryEntry: true,
          })
        } else {
          // When clearing all filters, preserve view or status (but not both)
          writeStoredFilterParam(filterStorageKey, null)

          const currentViewParam = keys.view && urlParamsRef.current[keys.view]
          const currentStatus = keys.status && urlParamsRef.current[keys.status]

          const removeParams = [keys.filter, keys.risk, keys.status].filter(Boolean) as string[]

          if (currentViewParam && keys.view) {
            // If we have a view, only preserve the view (not individual status)
            replaceParamsRef.current({
              update: [[keys.view, currentViewParam]],
              remove: removeParams,
              replaceCurrentHistoryEntry: true,
            })
          } else if (currentStatus && keys.status) {
            // No view, preserve individual status
            replaceParamsRef.current({
              update: [[keys.status, currentStatus]],
              remove: [keys.filter, keys.risk].filter(Boolean) as string[],
              replaceCurrentHistoryEntry: true,
            })
          } else {
            // No view or status to preserve
            replaceParamsRef.current({
              remove: removeParams,
              replaceCurrentHistoryEntry: true,
            })
          }
        }
      }, delays.urlUpdate)
    }

    const onSortChanged = (): void => {
      // Skip URL sync if a view is being applied to prevent conflicts
      if (isApplyingViewRef.current) {
        return
      }

      const keys = keysRef.current
      const serializers = serializersRef.current
      const sortModel = getSortModelFromColumnState(gridApi)
      const currentViewParam = keys.view && urlParamsRef.current[keys.view]
      const currentFilter = keys.filter && urlParamsRef.current[keys.filter]

      const update: Array<[string, string]> = []
      const remove: string[] = []

      // Preserve view if present
      if (currentViewParam && keys.view) {
        update.push([keys.view, currentViewParam])
      }

      // Preserve unified filter if present
      if (currentFilter && keys.filter) {
        update.push([keys.filter, currentFilter])
      }

      // Add sort if present
      if (keys.sort) {
        const sortParam = serializers.sortModelToUrlParam(sortModel)
        writeStoredSort(filterStorageKeyRef.current, sortParam)
        if (sortParam) {
          update.push([keys.sort, sortParam])
        } else {
          remove.push(keys.sort)
        }
      }

      // Remove legacy parameters to maintain consistency
      const legacyParams = [keys.risk, keys.status].filter(Boolean) as string[]
      remove.push(...legacyParams)

      replaceParamsRef.current({
        update,
        remove,
        replaceCurrentHistoryEntry: true,
      })
    }

    const onColumnStateChanged = (): void => {
      // Skip URL sync if a view is being applied to prevent conflicts
      if (isApplyingViewRef.current) {
        return
      }

      // Only sync if column customization is enabled (allColumnIds provided)
      if (allColumnIdsRef.current.length === 0) {
        return
      }

      // Debounce URL updates to prevent excessive history entries
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        const keys = keysRef.current
        const serializers = serializersRef.current
        const filterStorageKey = filterStorageKeyRef.current
        const columnState = getColumnStateFromGrid(gridApi)
        const sortModel = getSortModelFromColumnState(gridApi)
        const currentViewParam = keys.view && urlParamsRef.current[keys.view]
        const currentFilter = keys.filter && urlParamsRef.current[keys.filter]

        const update: Array<[string, string]> = []
        const remove: string[] = []

        // Preserve view if present
        if (currentViewParam && keys.view) {
          update.push([keys.view, currentViewParam])
        }

        // Preserve unified filter if present
        if (currentFilter && keys.filter) {
          update.push([keys.filter, currentFilter])
        }

        // Preserve sort if present
        if (keys.sort) {
          const sortParam = serializers.sortModelToUrlParam(sortModel)
          if (sortParam) {
            writeStoredSort(filterStorageKey, sortParam)
            update.push([keys.sort, sortParam])
          }
        }

        // Add column state if present and different from default
        if (keys.columns) {
          const columnParam = serializers.columnStateToUrlParam(columnState)
          if (columnParam) {
            update.push([keys.columns, encodeURIComponent(columnParam)])
          } else {
            remove.push(keys.columns)
          }
        }

        // Remove legacy parameters to maintain consistency
        const legacyParams = [keys.risk, keys.status].filter(Boolean) as string[]
        remove.push(...legacyParams)

        replaceParamsRef.current({
          update,
          remove,
          replaceCurrentHistoryEntry: true,
        })
      }, delays.urlUpdate)
    }

    // Add a delay before setting up the listeners to allow initial view restoration to complete
    const setupTimeout = setTimeout(() => {
      gridApi.addEventListener('filterChanged', onFilterChanged)
      gridApi.addEventListener('sortChanged', onSortChanged)
      gridApi.addEventListener('columnMoved', onColumnStateChanged)
      gridApi.addEventListener('columnVisible', onColumnStateChanged)

      // Restore ran before these listeners; if the grid dropped requested values, rewrite the URL to match it.
      const { params } = getFilterSourceParams(
        filterStorageKeyRef.current,
        keysRef.current.filter,
        urlParamsRef.current,
      )
      const requested = serializersRef.current.urlToFilterModel(params)
      const applied = withoutEmptySetFilters(gridApi.getFilterModel() as FilterModel)
      if (!appliesFilterModel(requested, applied)) onFilterChanged()
    }, delays.listenerSetup)

    return (): void => {
      clearTimeout(setupTimeout)
      gridApi.removeEventListener('filterChanged', onFilterChanged)
      gridApi.removeEventListener('sortChanged', onSortChanged)
      gridApi.removeEventListener('columnMoved', onColumnStateChanged)
      gridApi.removeEventListener('columnVisible', onColumnStateChanged)
    }
  }, [enabled, gridApi, isApplyingViewRef, delays.urlUpdate, delays.listenerSetup])

  // Restore filters from URL - only for cases where there's no view (default view)
  useEffect(() => {
    if (!enabled || !gridApi || viewInitializedRef.current) {
      return
    }

    // Only proceed if restoration is ready (grid API available)
    if (!isRestored) {
      return
    }

    const viewFromUrl = keys.view && urlParams[keys.view]

    // If there's a view in the URL, let view management handle the restoration
    if (viewFromUrl) {
      viewInitializedRef.current = true
      return
    }

    // URL wins; fall back to localStorage when navigating back without query params
    const { resolved, params: filterParams } = getFilterSourceParams(
      filterStorageKey,
      keys.filter,
      urlParams,
    )
    const { resolved: resolvedSort } = getStoredSourceParams(
      filterStorageKey,
      keys.sort,
      urlParams,
      'sort',
    )
    const hasFilterParams =
      !!resolved || (keys.risk && urlParams[keys.risk]) || (keys.status && urlParams[keys.status])
    const hasSortParam = !!resolvedSort
    const columnParam = keys.columns ? urlParams[keys.columns] : undefined
    const hasColumnParam = !!columnParam

    if ((hasFilterParams || hasSortParam || hasColumnParam) && currentView === defaultView) {
      try {
        // Restore column state first (before filters/sort)
        if (hasColumnParam && allColumnIds.length > 0) {
          const columnState = serializers.urlParamToColumnState(
            decodeURIComponent(columnParam),
            allColumnIds,
          )
          if (columnState) {
            gridApi.applyColumnState({
              state: columnState as ColumnState[],
              applyOrder: true,
            })
          }
        }

        // Restore filters
        if (hasFilterParams) {
          const filterModel = serializers.urlToFilterModel(filterParams)
          gridApi.setFilterModel(filterModel)
        }

        // Restore sort
        // Compact column state has no sort — apply it after columns so
        // applyOrder does not wipe the direction.
        if (hasSortParam) {
          applySortModelViaColumnState(gridApi, serializers.urlParamToSortModel(resolvedSort))
        }

        const update: Array<[string, string]> = []
        if (keys.filter && resolved) {
          if (!urlParams[keys.filter]) update.push([keys.filter, resolved])
          else writeStoredFilterParam(filterStorageKey, resolved)
        }
        if (keys.sort && resolvedSort) {
          if (!urlParams[keys.sort]) update.push([keys.sort, resolvedSort])
          else writeStoredSort(filterStorageKey, resolvedSort)
        }
        if (update.length > 0) {
          replaceParams({ update, replaceCurrentHistoryEntry: true })
        }

        viewInitializedRef.current = true
      } catch (e) {
        reportErrors(
          e instanceof Error
            ? e
            : new Error('[AgUrlSync] Failed to parse filter, sort, or column state from URL'),
        )
        viewInitializedRef.current = true
      }
    } else {
      viewInitializedRef.current = true
    }
  }, [
    enabled,
    gridApi,
    urlParams,
    currentView,
    defaultView,
    serializers,
    keys,
    isRestored,
    reportErrors,
    allColumnIds,
    replaceParams,
    filterStorageKey,
  ])

  // Mark restoration as ready when grid API is available
  useEffect(() => {
    if (gridApi && !isRestored) {
      // oxlint-disable-next-line react/set-state-in-effect
      setIsRestored(true)
    }
  }, [gridApi, isRestored])

  // Return manager interface (minimal for now since it's primarily side-effect driven)
  return {
    isRestored,
    isUpdatingUrl,
  }
}
